import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ClipboardCheck, RefreshCw, Save, CheckCircle, Info } from 'lucide-react';

/**
 * RH · Avaliação de Desempenho — PREENCHIMENTO.
 * Escolhe o funcionário (dos dois cargos), carrega a versão Publicada do cargo,
 * pontua cada item de 1 a 5 e finaliza. A nota é calculada no backend; aqui há
 * uma PRÉVIA ao vivo (espelho do motor desempenho-score.js). Faltas puxadas do
 * espelho por período, com override. Cor RH #d81d1d.
 */
const API = '/api/rh/desempenho';
const COR = '#d81d1d';
const FALTAS_DIVISOR = 264;
const CARGOS = [
    { abrev: 'COORD. QUAL E SERV', label: 'Coordenador Q&S' },
    { abrev: 'GERENTE PLANTAO', label: 'Gerente de Plantão' },
];
const ESCALA = [
    { n: 1, txt: 'Nunca' }, { n: 2, txt: 'Em baixo grau' }, { n: 3, txt: 'Até certo ponto' },
    { n: 4, txt: 'Em alto grau' }, { n: 5, txt: 'Em altíssimo grau' },
];
const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` });
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Prévia local — MESMA fórmula do backend (que é a autoridade ao gravar).
const calcularPrevia = (blocos, respostas, faltas) => {
    const bl = blocos.map((b) => {
        const notas = b.itens.map((i) => respostas[i.ID]).filter((n) => n != null);
        if (notas.length === 0) return { nota: null };
        return { nota: round2((notas.reduce((s, n) => s + n, 0) / notas.length / 5) * 100) };
    });
    let avaliacao = null;
    if (bl.length > 0 && bl.every((b) => b.nota != null)) {
        avaliacao = round2(blocos.reduce((s, b, i) => s + Number(b.PESO) * bl[i].nota, 0));
    }
    const nf = Number(faltas);
    const desconto = Number.isFinite(nf) && nf > 0 ? round2((nf / FALTAS_DIVISOR) * 100) : 0;
    const notaFinal = avaliacao == null ? null : round2(Math.min(Math.max(avaliacao - desconto, 0), 100));
    return { blocos: bl, avaliacao, desconto, notaFinal };
};

const RHDesempenhoAvaliacao = () => {
    const [cargo, setCargo] = useState(CARGOS[0].abrev);
    const [funcionarios, setFuncionarios] = useState([]);
    const [funcId, setFuncId] = useState('');
    const [versao, setVersao] = useState(null);
    const [blocos, setBlocos] = useState([]);
    const [respostas, setRespostas] = useState({}); // ID_ITEM -> 1..5
    const [avaliador, setAvaliador] = useState('');
    const [periodoInicio, setPeriodoInicio] = useState('');
    const [periodoFim, setPeriodoFim] = useState('');
    const [faltas, setFaltas] = useState('');
    const [faltasAuto, setFaltasAuto] = useState(null); // sugestão do espelho
    const [observacoes, setObservacoes] = useState('');
    const [plano, setPlano] = useState('');
    const [loadingFunc, setLoadingFunc] = useState(false);
    const [loadingForm, setLoadingForm] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const listarFunc = useCallback(async () => {
        setLoadingFunc(true);
        try {
            const res = await fetch(`${API}/funcionarios?cargo=${encodeURIComponent(cargo)}`, { headers: authHeaders() });
            setFuncionarios(res.ok ? await res.json() : []);
        } catch { setFuncionarios([]); } finally { setLoadingFunc(false); }
    }, [cargo]);

    useEffect(() => { listarFunc(); setFuncId(''); setVersao(null); setBlocos([]); }, [listarFunc]);

    // Abre o formulário ao escolher o funcionário.
    useEffect(() => {
        if (!funcId) { setVersao(null); setBlocos([]); return; }
        setLoadingForm(true); setFeedback(null); setRespostas({});
        (async () => {
            try {
                const res = await fetch(`${API}/avaliacao/funcionario/${funcId}`, { headers: authHeaders() });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || 'Erro ao abrir avaliação.');
                setVersao(data.versao); setBlocos(data.blocos || []);
            } catch (e) { setFeedback({ ok: false, msg: e.message }); setVersao(null); setBlocos([]); }
            finally { setLoadingForm(false); }
        })();
    }, [funcId]);

    // Sugere as faltas do período (só quando o período está completo).
    const buscarFaltas = useCallback(async () => {
        if (!funcId || !periodoInicio || !periodoFim) return;
        try {
            const res = await fetch(`${API}/faltas/funcionario/${funcId}?inicio=${periodoInicio}&fim=${periodoFim}`, { headers: authHeaders() });
            const d = await res.json();
            setFaltasAuto(d.faltas);
            if (d.faltas != null && faltas === '') setFaltas(String(d.faltas));
        } catch { /* sem dado — segue com override manual */ }
    }, [funcId, periodoInicio, periodoFim, faltas]);
    useEffect(() => { buscarFaltas(); }, [buscarFaltas]);

    const previa = useMemo(() => calcularPrevia(blocos, respostas, faltas === '' ? null : Number(faltas)), [blocos, respostas, faltas]);
    const totalItens = useMemo(() => blocos.reduce((s, b) => s + b.itens.length, 0), [blocos]);
    const respondidos = Object.keys(respostas).length;

    const salvar = async (finalizar) => {
        setSalvando(true); setFeedback(null);
        try {
            const res = await fetch(`${API}/avaliacao`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({
                    idFuncionario: Number(funcId), idVersao: versao.ID, avaliador, periodoInicio, periodoFim,
                    faltas: faltas === '' ? null : Number(faltas), respostas, observacoes, planoDesenvolvimento: plano, finalizar,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Erro ao gravar.');
            setFeedback({ ok: true, msg: finalizar ? `Avaliação finalizada — nota ${data.notaFinal}.` : 'Rascunho salvo.' });
            if (finalizar) { setFuncId(''); setRespostas({}); setFaltas(''); setObservacoes(''); setPlano(''); }
        } catch (e) { setFeedback({ ok: false, msg: e.message }); } finally { setSalvando(false); }
    };

    const label = { display: 'block', fontSize: '12px', fontWeight: 700, color: '#694747', marginBottom: '4px' };
    const inp = { width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1px solid #f0e2e2', fontSize: '13px', boxSizing: 'border-box' };

    return (
        <div style={{ maxWidth: '900px' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div style={{ flex: '0 0 200px' }}>
                    <label style={label}>Cargo</label>
                    <select value={cargo} onChange={(e) => setCargo(e.target.value)} style={inp}>
                        {CARGOS.map((c) => <option key={c.abrev} value={c.abrev}>{c.label}</option>)}
                    </select>
                </div>
                <div style={{ flex: '1 1 260px' }}>
                    <label style={label}>Funcionário {loadingFunc && '(carregando…)'}</label>
                    <select value={funcId} onChange={(e) => setFuncId(e.target.value)} style={inp}>
                        <option value=''>Selecione… ({funcionarios.length})</option>
                        {funcionarios.map((f) => <option key={f.ID_FUNCIONARIO} value={f.ID_FUNCIONARIO}>{f.NOME_PESSOA} — {f.CODIGO_LOCALIZACAO}</option>)}
                    </select>
                </div>
                <button onClick={listarFunc} style={{ alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #f0e2e2', background: '#fff', color: '#8b6464', fontWeight: 600, cursor: 'pointer' }}><RefreshCw size={14} /></button>
            </div>

            {feedback && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', backgroundColor: feedback.ok ? '#d1fae5' : '#fee2e2', color: feedback.ok ? '#065f46' : '#b91c1c', fontWeight: 600 }}>
                    {feedback.ok ? <CheckCircle size={18} /> : <Info size={18} />} {feedback.msg}
                </div>
            )}

            {loadingForm ? <p style={{ color: '#8b6464' }}>Carregando o formulário…</p> : !versao ? (
                funcId ? null : <div style={{ textAlign: 'center', padding: '48px', color: '#8b6464' }}><ClipboardCheck size={40} color="#b89494" style={{ marginBottom: '12px' }} /><p>Escolha um funcionário para avaliar.</p></div>
            ) : (
                <>
                    <div style={{ fontSize: '13px', color: '#8b6464', marginBottom: '14px' }}>Modelo: <strong>{versao.NOME_VERSAO}</strong></div>

                    {/* Período + avaliador + faltas */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
                        <div style={{ flex: '1 1 180px' }}><label style={label}>Avaliador</label><input value={avaliador} onChange={(e) => setAvaliador(e.target.value)} style={inp} /></div>
                        <div style={{ flex: '0 0 150px' }}><label style={label}>Período — início</label><input type="date" value={periodoInicio} onChange={(e) => setPeriodoInicio(e.target.value)} style={inp} /></div>
                        <div style={{ flex: '0 0 150px' }}><label style={label}>Período — fim</label><input type="date" value={periodoFim} onChange={(e) => setPeriodoFim(e.target.value)} style={inp} /></div>
                        <div style={{ flex: '0 0 130px' }}>
                            <label style={label}>Faltas (dias)</label>
                            <input type="number" min="0" value={faltas} onChange={(e) => setFaltas(e.target.value)} style={inp} placeholder="—" />
                            <div style={{ fontSize: '11px', color: '#b89494', marginTop: '3px' }}>{faltasAuto != null ? `Espelho: ${faltasAuto}` : 'Sem dado do espelho'}</div>
                        </div>
                    </div>

                    {/* Blocos e itens */}
                    {blocos.map((b) => (
                        <div key={b.ID} style={{ border: '1px solid #f0e2e2', borderRadius: '10px', marginBottom: '16px', overflow: 'hidden' }}>
                            <div style={{ padding: '10px 14px', backgroundColor: '#fcf8f8', fontWeight: 700, color: '#0f172a', display: 'flex', justifyContent: 'space-between' }}>
                                <span>{b.NOME}</span><span style={{ color: COR }}>{Math.round(Number(b.PESO) * 100)}%</span>
                            </div>
                            {b.itens.map((it) => (
                                <div key={it.ID} style={{ padding: '12px 14px', borderTop: '1px solid #f9f1f1' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>{it.CODIGO ? `${it.CODIGO} · ` : ''}{it.ROTULO}</div>
                                    {it.DESCRICAO && <div style={{ fontSize: '12px', color: '#8b6464', marginBottom: '8px' }}>{it.DESCRICAO}</div>}
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        {ESCALA.map((e) => {
                                            const sel = respostas[it.ID] === e.n;
                                            return (
                                                <button key={e.n} title={e.txt} onClick={() => setRespostas((r) => ({ ...r, [it.ID]: e.n }))}
                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: '7px', border: `1px solid ${sel ? COR : '#e2cfcf'}`, backgroundColor: sel ? COR : '#fff', color: sel ? '#fff' : '#94a3b8', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                                                    {e.n} <span style={{ fontWeight: 400, fontSize: '11px' }}>{e.txt}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}

                    <div style={{ marginBottom: '14px' }}><label style={label}>Observações</label><textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} /></div>
                    <div style={{ marginBottom: '18px' }}><label style={label}>Plano de desenvolvimento</label><textarea value={plano} onChange={(e) => setPlano(e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} /></div>

                    {/* Prévia da nota */}
                    <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center', padding: '16px', borderRadius: '10px', marginBottom: '18px', backgroundColor: '#fcf8f8', border: '1px solid #f0e2e2' }}>
                        <span style={{ fontSize: '13px', color: '#475569' }}>Respondidos <strong>{respondidos}/{totalItens}</strong></span>
                        {previa.blocos.map((b, i) => <span key={i} style={{ fontSize: '13px', color: '#475569' }}>{blocos[i].NOME.split(' ')[1] || blocos[i].NOME}: <strong>{b.nota ?? '—'}</strong></span>)}
                        <span style={{ fontSize: '13px', color: '#475569' }}>Desconto faltas <strong>{previa.desconto}</strong></span>
                        <span style={{ fontSize: '15px', color: '#0f172a' }}>Nota final <strong style={{ fontSize: '20px', color: COR }}>{previa.notaFinal ?? '—'}</strong></span>
                        <span style={{ fontSize: '11px', color: '#8b6464' }}>(prévia — o backend recalcula ao salvar)</span>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button onClick={() => salvar(false)} disabled={salvando} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 18px', borderRadius: '8px', border: '1px solid #f0e2e2', backgroundColor: '#fff', color: '#694747', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}><Save size={16} /> Salvar rascunho</button>
                        <button onClick={() => salvar(true)} disabled={salvando || respondidos < totalItens} title={respondidos < totalItens ? 'Responda todos os itens' : ''} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: (salvando || respondidos < totalItens) ? '#fd9393' : COR, color: '#fff', fontSize: '14px', fontWeight: 700, cursor: (salvando || respondidos < totalItens) ? 'not-allowed' : 'pointer' }}><CheckCircle size={16} /> {salvando ? 'Salvando…' : 'Finalizar'}</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default RHDesempenhoAvaliacao;
